import React, { useContext, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import { IconType } from 'react-icons';
import {
  FiArrowRight,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiLock,
  FiMapPin,
  FiMic,
  FiShield,
} from 'react-icons/fi';
import { Box, Button, Typography, makeStyles, Theme, createStyles, useMediaQuery } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';

import { LailaMessage } from '../../../types';
import { DeviceContext } from '../../../contexts';
import { AIChatContext } from '../context';
import { LailaOrb, LailaOrbState } from './launcher/LailaOrb';
import { LailaBubble } from './thread/LailaBubble';
import { LailaTyping } from './thread/LailaTyping';

interface Feature {
  icon: IconType;
  title: string;
  text: string;
}

interface Scene {
  question: string;
  answer: string;
  file?: { name: string; size: number };
}

// Only what Laila can do today: the employer side has clock-in and time-log tools.
const EMPLOYER_FEATURES: Feature[] = [
  {
    icon: FiClock,
    title: 'Live answers about your workforce',
    text: "Who's on site, who's clocked out and the hours logged, straight from Vyce.",
  },
  {
    icon: FiShield,
    title: 'Compliance in plain English',
    text: 'CDM 2015, CIS and right to work, explained without the jargon.',
  },
  {
    icon: FiFileText,
    title: 'Documents read for you',
    text: 'Drop in a contract and get the key terms and risks back in seconds.',
  },
  { icon: FiMic, title: 'Type or talk', text: 'Ask out loud when your hands are full.' },
];
const WORKER_FEATURES: Feature[] = [
  {
    icon: FiCreditCard,
    title: 'Your cards and tickets',
    text: 'What your CSCS card covers and how to move up a level.',
  },
  {
    icon: FiMapPin,
    title: 'Ready for site',
    text: 'What to bring and what to expect at your next induction.',
  },
  {
    icon: FiFileText,
    title: 'Contracts checked',
    text: 'Drop in a subcontract and see what to question before you sign.',
  },
  { icon: FiMic, title: 'Type or talk', text: 'Ask out loud when your hands are full.' },
];

// The demo chat beside the copy: illustrative questions and answers, not real data.
const EMPLOYER_SCENES: Scene[] = [
  {
    question: "Who's clocked in right now?",
    answer: '**14 people** are on site: 9 at Kings Cross and 5 at Wembley. Want the list?',
  },
  {
    question: 'What do we need for CDM 2015?',
    answer:
      'A construction phase plan, site inductions and a named principal contractor. I can make you a checklist.',
  },
  {
    question: 'Any red flags in this contract?',
    answer:
      'Payment in 30 days and 5% retention are standard. The **pay-when-paid clause** is worth pushing back on.',
    file: { name: 'Subcontract.pdf', size: 482000 },
  },
];
const WORKER_SCENES: Scene[] = [
  {
    question: 'What does my CSCS card let me do?',
    answer: 'A **green card** covers labouring on site. An NVQ Level 2 gets you a blue skilled worker card.',
  },
  {
    question: 'Explain CIS deductions simply',
    answer:
      'Your contractor holds back **20%** of your labour pay and sends it to HMRC for you. It counts towards your tax.',
  },
  {
    question: 'Anything to check before I sign?',
    answer: 'The day rate and payment terms look fine. Ask who pays for **lost time** if the site shuts.',
    file: { name: 'Subcontract.pdf', size: 482000 },
  },
];

// One scene plays out like a real chat: the question, Laila thinking, her answer.
type Step = 'asked' | 'typing' | 'answered' | 'done' | 'leaving';
const TIMELINE: [Step, number][] = [
  ['asked', 0],
  ['typing', 700],
  ['answered', 2100],
  ['done', 4300],
  ['leaving', 6400],
];
const SCENE_LENGTH = 6800;

const ORB_STATES: Record<Step, LailaOrbState> = {
  asked: 'idle',
  typing: 'thinking',
  answered: 'speaking',
  done: 'idle',
  leaving: 'idle',
};

const toMessage = (id: string, role: LailaMessage['role'], text: string, scene?: Scene): LailaMessage => ({
  id,
  role,
  blocks: [{ type: 'text', text }],
  files: scene?.file ? [{ fileId: id, name: scene.file.name, size: scene.file.size } as any] : undefined,
  createdAt: 0,
});

interface Props {
  onStartClick: Function;
}

export const GetStarted = ({ onStartClick }: Props) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const { user } = useContext(AIChatContext);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const isEmployer = !!user?.positions?.length;
  const features = isEmployer ? EMPLOYER_FEATURES : WORKER_FEATURES;
  const scenes = isEmployer ? EMPLOYER_SCENES : WORKER_SCENES;

  const [sceneIndex, setSceneIndex] = useState(0);
  const [step, setStep] = useState<Step>('asked');
  const [level, setLevel] = useState(0);
  const scene = scenes[sceneIndex % scenes.length];

  useEffect(() => {
    if (reducedMotion) {
      setStep('done');
      return;
    }
    const timers = TIMELINE.map(([next, at]) => setTimeout(() => setStep(next), at));
    timers.push(setTimeout(() => setSceneIndex(index => index + 1), SCENE_LENGTH));
    return () => timers.forEach(clearTimeout);
  }, [sceneIndex, reducedMotion]);

  // While Laila "speaks" her answer the orb swells as if with a voice.
  useEffect(() => {
    if (step !== 'answered' || reducedMotion) {
      setLevel(0);
      return;
    }
    const timer = setInterval(() => setLevel(0.2 + Math.random() * 0.7), 120);
    return () => clearInterval(timer);
  }, [step, reducedMotion]);

  const question = useMemo(() => toMessage(`q${sceneIndex}`, 'user', scene.question, scene), [sceneIndex]);
  const answer = useMemo(() => toMessage(`a${sceneIndex}`, 'assistant', scene.answer), [sceneIndex]);
  const answered = step === 'answered' || step === 'done' || step === 'leaving';

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.eyebrow}>Vyce AI</Typography>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className={classes.title}>
          Meet Laila, your AI assistant
        </Typography>
        <Typography color="textSecondary" className={classes.lead}>
          Ask anything about your work in plain English and get a clear answer in seconds.
        </Typography>
      </Box>

      <Box className={classes.body}>
        <Box component="ul" className={classes.features}>
          {features.map(({ icon: Icon, title, text }) => (
            <Box component="li" key={title} className={classes.feature}>
              <span className={classes.featureIcon}>
                <Icon size={18} />
              </span>
              <span>
                <Typography variant="subtitle2" component="span" className={classes.featureTitle}>
                  {title}
                </Typography>
                <Typography variant="body2" color="textSecondary" component="span">
                  {text}
                </Typography>
              </span>
            </Box>
          ))}
        </Box>

        <Button
          size="large"
          variant="contained"
          color="primary"
          cy-test-id="holding-page-button"
          onClick={() => onStartClick()}
          endIcon={<FiArrowRight />}
          fullWidth={isMobile}
          className={classes.button}
        >
          Start chatting
        </Button>

        <Typography variant="caption" color="textSecondary" className={classes.note}>
          <FiLock size={12} />
          Laila only sees what you can see in Vyce.
        </Typography>
      </Box>

      <Box className={classes.stage} aria-hidden>
        <Box className={classes.glow} />
        <LailaOrb
          size={isMobile ? 80 : 152}
          state={ORB_STATES[step]}
          level={level}
          className={classes.orb}
        />

        <Box className={classes.demo}>
          <Box className={clsx(classes.demoThread, { [classes.demoLeaving]: step === 'leaving' })}>
            <LailaBubble key={question.id} message={question} first last copyable={false} />
            {step === 'typing' && <LailaTyping />}
            {answered && <LailaBubble key={answer.id} message={answer} first last copyable={false} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// The app bar is 64px tall, 56px on phones.
const useStyles = makeStyles((theme: Theme) => {
  const brand = theme.palette.primary.main;

  return createStyles({
    root: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gridTemplateAreas: '"header stage" "body stage"',
      gridTemplateRows: 'auto auto',
      alignContent: 'center',
      columnGap: 64,
      // The glow reaches past the stage; it must not widen the page.
      overflowX: 'hidden',
      maxWidth: 1120,
      margin: '0 auto',
      padding: '32px 24px',
      fallbacks: { minHeight: 'calc(100vh - 64px)' },
      minHeight: 'calc(100dvh - 64px)',
      animation: '$enter 320ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateAreas: '"header" "stage" "body"',
        gridTemplateRows: 'auto',
        alignContent: 'start',
        maxWidth: 560,
        padding: '16px 0 24px',
      },
      [theme.breakpoints.down('xs')]: {
        fallbacks: { minHeight: 'calc(100vh - 56px)' },
        minHeight: 'calc(100dvh - 56px)',
      },
    } as any,
    header: { gridArea: 'header', alignSelf: 'end' },
    body: {
      gridArea: 'body',
      alignSelf: 'start',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    eyebrow: {
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: brand,
    },
    title: { marginTop: 8, fontWeight: 700, lineHeight: 1.2 },
    lead: { marginTop: 12, fontSize: 17, maxWidth: 460 },
    features: {
      listStyle: 'none',
      margin: '28px 0 32px',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      [theme.breakpoints.down('xs')]: { margin: '20px 0 24px', gap: 14 },
    },
    feature: { display: 'flex', alignItems: 'flex-start', gap: 14 },
    featureIcon: {
      flex: 'none',
      width: 36,
      height: 36,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: brand,
      backgroundColor: alpha(brand, theme.palette.type === 'dark' ? 0.2 : 0.1),
    },
    featureTitle: { display: 'block', fontWeight: 600, color: theme.palette.text.primary },
    button: { minWidth: 220, height: 48, borderRadius: 12, fontWeight: 600 },
    note: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginTop: 14,
    },
    stage: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      maxWidth: 440,
      gridArea: 'stage',
      alignSelf: 'center',
      justifySelf: 'center',
    },
    // A soft wash of brand colour behind the orb and the demo.
    glow: {
      position: 'absolute',
      top: '-10%',
      left: '50%',
      width: '130%',
      height: '90%',
      transform: 'translateX(-50%)',
      borderRadius: '50%',
      background: `radial-gradient(closest-side, ${alpha(brand, 0.14)}, ${alpha(brand, 0)})`,
      pointerEvents: 'none',
    },
    // Room for the halo, which reaches well past the orb.
    orb: {
      margin: '56px 0 52px',
      [theme.breakpoints.down('sm')]: { margin: '40px 0 32px' },
    },
    demo: {
      position: 'relative',
      width: '100%',
      height: 248,
      padding: 16,
      boxSizing: 'border-box',
      overflow: 'hidden',
      borderRadius: 24,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      boxShadow: `0 12px 40px ${alpha(
        theme.palette.common.black,
        theme.palette.type === 'dark' ? 0.4 : 0.08
      )}`,
      [theme.breakpoints.down('xs')]: { height: 232, padding: 12, borderRadius: 20 },
    },
    // Messages sit at the bottom, like the end of a conversation.
    demoThread: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      height: '100%',
      transition: 'opacity 300ms, transform 300ms',
    },
    demoLeaving: { opacity: 0, transform: 'translateY(-8px)' },
    '@keyframes enter': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'none' },
    },
  });
});
